// @flow

import * as React from 'react';
import throttle from '@ta/common.throttle';

/** The component props for a <ParentScroll> */
export type Props = {|
  /** The callback function for when a parent 'scroll' event is fired */
  callback: () => mixed,

  /** Optional: The minimum number of milliseconds between invocations of the callback */
  throttle: number,

  /**
   * Render prop accepting a "target" function, which should be assigned as the 'ref' prop of the
   * element you're interested in. Will render any React Node returned.
   */
  children: ((?HTMLElement) => void) => React.Node,

  /** disables listeners when true. This stops the chatty scroll handlers once you don't need to be notified anymore */
  disableListeners?: boolean,
|};

/**
 * Event handler component that listens for 'scroll' event on a scrollable parent div. You provide a
 * callback along with a throttle to limit the rate of callbacks
 */
export default class ParentScroll extends React.Component<Props> {
  static defaultProps = {
    throttle: 200,
    disableListeners: false,
  };

  componentDidUpdate() {
    if (this.props.disableListeners) {
      this.disableListeners();
    }
  }

  componentWillUnmount() {
    this.disableListeners();
  }

  disableListeners() {
    if (this.scrollable != null) {
      this.scrollable.removeEventListener('scroll', this.handleScroll);
    }
  }

  scrollable = null;

  // For this function code coverage was disabled since it's almost impossible to mock window methods used from isScrollable
  addScrollEventHandler = /* istanbul ignore next */ (element: any) => {
    /* eslint-disable no-restricted-globals */
    let cur = element;
    while (cur !== null && cur !== document) {
      if (this.isScrollable(cur)) {
        this.scrollable = cur;
        break;
      }
      cur = cur.parentNode;
    }

    if (this.scrollable != null) {
      this.scrollable.addEventListener('scroll', this.handleScroll);
    }
    /* eslint-enable no-restricted-globals */
  };

  // For this function code coverage was disabled since it's almost impossible to mock window methods
  isScrollable = /* istanbul ignore next */ (element: any) => {
    const overflowRegex = /(auto|overlay|scroll)/;

    /* eslint-disable no-restricted-globals */
    const style = window.getComputedStyle(element);

    // check against horizontal scroll
    if (overflowRegex.test(style.getPropertyValue('overflow-x'))) {
      // check if the width is less than scrollWidth
      if (element.scrollWidth > element.clientWidth) {
        return true;
      }
    }

    // check against vertical scroll
    if (overflowRegex.test(style.getPropertyValue('overflow-y'))) {
      // check if the height is less than scrollHeight
      if (element.scrollHeight > element.clientHeight) {
        return true;
      }
    }

    /* eslint-enable no-restricted-globals */
    return false;
  };

  handleScroll = throttle(() => {
    this.props.callback();
  }, this.props.throttle);

  render() {
    return this.props.children(el => {
      if (el && !this.props.disableListeners) {
        this.addScrollEventHandler(el);
      }
    });
  }
}
